import * as Sentry from "@sentry/react";

// Define your backend URL
const backendUrl = import.meta.env.VITE_API_URL || "http://localhost:8000";
const environment = import.meta.env.VITE_SENTRY_ENV || "dev";

function getDsnDiagnostics(dsnRaw: unknown) {
  const dsn = typeof dsnRaw === "string" ? dsnRaw.trim() : "";
  const isCiphertextLike = dsn.startsWith("AQICAH");
  let host = "";
  let isValidShape = false;

  try {
    const parsed = new URL(dsn);
    isValidShape =
      (parsed.protocol === "https:" || parsed.protocol === "http:") &&
      Boolean(parsed.host);
    host = parsed.host;
  } catch {
    isValidShape = false;
  }

  return {
    dsn,
    loaded: dsn.length > 0,
    isCiphertextLike,
    isValidShape,
    host: host || "unknown",
  };
}

/**
 * Initialize Sentry for frontend error tracking and performance monitoring
 *
 * This function configures Sentry with:
 * - Error and crash reporting via DSN
 * - Performance monitoring through browser tracing and profiling
 * - Environment-specific settings
 * - Trace propagation rules to maintain context across frontend and backend
 *
 * Note: Sentry is disabled in development environments
 */
export function initSentry() {
  // Skip Sentry initialization in development environments
  if (environment === "dev") {
    console.warn("Sentry disabled in development environment");
    return;
  }

  // Defensive guard only; root fix is SSM decryption at build/deploy time.
  const dsnDiagnostics = getDsnDiagnostics(import.meta.env.VITE_SENTRY_DSN);
  if (
    !dsnDiagnostics.loaded ||
    dsnDiagnostics.isCiphertextLike ||
    !dsnDiagnostics.isValidShape
  ) {
    console.error(
      "Sentry disabled due to invalid frontend DSN. Check CI SSM decryption and build-time env injection.",
      {
        dsn_loaded: dsnDiagnostics.loaded,
        dsn_is_ciphertext_like: dsnDiagnostics.isCiphertextLike,
        dsn_valid_shape: dsnDiagnostics.isValidShape,
        dsn_host: dsnDiagnostics.host,
      },
    );
    return;
  }

  Sentry.init({
    // Data Source Name - the unique identifier that tells Sentry where to send events
    dsn: dsnDiagnostics.dsn,

    // Core integrations for browser monitoring
    integrations: [
      // Tracks page loads, navigation events, and AJAX/fetch requests
      Sentry.browserTracingIntegration(),
      // Collects performance profiles to identify bottlenecks
      Sentry.browserProfilingIntegration(),
    ],

    // Sampling rate for performance transactions (0.1 = 10% of transactions)
    // Reduced for free plan quota management
    tracesSampleRate: 0.1,

    // Controls what percentage of traced transactions will be profiled
    // Final profiling rate = tracesSampleRate × profilesSampleRate
    profilesSampleRate: 0.1,

    // Restricts Sentry trace headers to specific targets for security
    // Prevents leaking trace info to third-party services
    tracePropagationTargets: [
      "localhost",
      /^\//, // Same-origin URLs
      backendUrl, // Your backend URL
    ],

    // Differentiates events between environments (dev, staging, production)
    environment,
  });
}
